import React, { useState } from 'react';
import { KBBlock } from '../../../types';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

function getDomain(url: string): string {
  try { return new URL(url).hostname.replace('www.', ''); } catch { return url; }
}

function getFavicon(url: string): string {
  try { const u = new URL(url); return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=32`; } catch { return ''; }
}

const BookmarkBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const url: string = block.data?.url ?? '';
  const title: string = block.data?.title ?? '';
  const description: string = block.data?.description ?? '';
  const [inputUrl, setInputUrl] = useState(url);

  if (isEditing) {
    return (
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center gap-2">
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          <input
            autoFocus
            type="url"
            value={inputUrl}
            placeholder="Paste a URL to bookmark…"
            onChange={(e) => setInputUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Escape') onFinish(); }}
            className="flex-1 border border-gray-200 rounded px-3 py-1.5 text-sm outline-none focus:border-blue-400"
          />
        </div>
        <input
          type="text"
          value={title}
          placeholder="Title (optional)"
          onChange={(e) => onChange({ data: { ...block.data, title: e.target.value } })}
          className="w-full border-b border-gray-200 pb-1 text-sm outline-none"
        />
        <input
          type="text"
          value={description}
          placeholder="Description (optional)"
          onChange={(e) => onChange({ data: { ...block.data, description: e.target.value } })}
          className="w-full border-b border-gray-200 pb-1 text-sm text-gray-500 outline-none"
        />
        <div className="flex gap-2">
          <button
            onClick={() => { onChange({ data: { ...block.data, url: inputUrl } }); onFinish(); }}
            className="text-xs bg-blue-600 text-white px-3 py-1 rounded"
          >
            Save bookmark
          </button>
          <button onClick={onFinish} className="text-xs text-gray-500 px-3 py-1 rounded border border-gray-200">Cancel</button>
        </div>
      </div>
    );
  }

  if (!url) {
    return (
      <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
        <span className="text-sm text-gray-300 italic">No bookmark URL</span>
      </div>
    );
  }

  const favicon = getFavicon(url);
  const domain = getDomain(url);

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 line-clamp-1">
          {title || url}
        </p>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{description}</p>
        )}
        <div className="flex items-center gap-1.5 mt-2">
          {favicon && <img src={favicon} alt="" className="w-4 h-4 rounded-sm" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
          <span className="text-xs text-gray-400">{domain}</span>
        </div>
      </div>
      <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
};

export default BookmarkBlock;
